import React, { useRef } from "react";
import TipTapSkeleton from "../TipTap/TipTapSkeleton";
import TitleInputSkeleton from "@/pages/Briefs/TitleInputSkeleton";

import { gsap } from "gsap";
import { useGSAP } from "@gsap/react";
import { SplitText } from "gsap/SplitText";
gsap.registerPlugin(SplitText);

interface BriefGenerationLoadingProps {
  genre?: string;
  style_vibe?: string;
  commissioned_by?: string;
  media_type?: string;
  lyrical_themes?: string;
  reference_tracks?: string;
  custom_instructions?: string;
}

const BriefGenerationLoading: React.FC<BriefGenerationLoadingProps> = ({
  genre,
  style_vibe,
  commissioned_by,
  media_type,
  lyrical_themes,
  reference_tracks,
  custom_instructions,
}) => {
  const loadingAnimationRef = useRef<HTMLDivElement>(null);
  const tl = useRef<gsap.core.Timeline>(gsap.timeline());

  const steps: { text: string; input: string }[] = [
    genre && { text: "for a ", input: `${genre} song` },
    style_vibe && { text: "that's ", input: style_vibe },
    commissioned_by && { text: "commissioned by ", input: commissioned_by },
    media_type && { text: "for ", input: media_type },
    lyrical_themes && {
      text: "with lyrical themes of ",
      input: lyrical_themes,
    },
    reference_tracks && {
      text: "referencing tracks like ",
      input: reference_tracks,
    },
    custom_instructions && {
      text: "with custom instructions: ",
      input: custom_instructions,
    },
  ].filter((step): step is { text: string; input: string } => Boolean(step));

  useGSAP(
    () => {
      const texts = gsap.utils.toArray(".loadingText") as HTMLElement[];
      const inputs = gsap.utils.toArray(".input") as HTMLElement[];

      tl.current = gsap.timeline();

      texts.forEach((text, i) => {
        const input = inputs[i];
        let color = `var(--color-peach-400)`;
        let split = SplitText.create(input, { type: "chars, words" });
        let dotsSplit = SplitText.create(".dots", { type: "chars" });

        const dotsTimeline = gsap.timeline({ repeat: -1, repeatDelay: 1 });

        dotsTimeline.to(dotsSplit.chars, {
          y: -5,
          duration: 0.25,
          color: color,
          stagger: {
            each: 0.06,
            repeat: 1,
            yoyo: true,
          },
        });

        tl.current
          .from(text, {
            yPercent: 50,
            autoAlpha: 0,
            duration: 0.5,
            ease: "power1.out",
          })
          .to(
            split.chars,
            {
              color: color,
              stagger: {
                each: 0.02,
                repeat: 1,
                yoyo: true,
                ease: "expo",
              },
            },
            "-=0.1",
          )
          .add(dotsTimeline, "-=0.5");
      });

      tl.current.from(".skeleton", {
        autoAlpha: 0,
        duration: 0.6,
        ease: "power1.out",
      });
    },
    { scope: loadingAnimationRef },
  );

  return (
    <>
      <div ref={loadingAnimationRef} className="flex w-full flex-col gap-5">
        <div className="flex flex-col gap-1">
          {steps.length === 0 ? (
            <span className="loadingText font-serif text-xl md:text-2xl">
              Generating a brief with{" "}
              <span className="input">who knows what</span>
              <span className="dots">...</span>
            </span>
          ) : (
            steps.map((step: { text: string; input: string }, i: number) => (
              <span
                key={i}
                className="loadingText font-serif text-xl md:text-2xl"
              >
                {i === 0 ? "Generating a brief " : ""}
                {step.text}
                <span className="input">{step.input}</span>
              </span>
            ))
          )}
        </div>
        <div className="skeleton flex flex-col gap-5">
          <TitleInputSkeleton />
          <TipTapSkeleton />
        </div>
      </div>
    </>
  );
};

export default BriefGenerationLoading;
